import React from "react";
import { getFileWorkflows, updateFileWorkflows } from "src/services/file.service";
import { Button, Stack } from "@mui/material";
import { Typography } from "@mui/material";
import TextShimmerMotion from "../shadcn-space/animated-text/animated-text-05";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import { getProfile } from "src/services/profile.service";

class FileWorkflows extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            updating: false,
            workflows: [],
            loggedInUserId: null
        };
    }

    async componentDidMount() {
        this.fetchUserProfile();
        await this.fetchFileWorkflows();
    }

    async componentDidUpdate(prevProps) {
        if (prevProps.file?._id !== this.props.file?._id) {
            await this.fetchFileWorkflows();
        }
    }

    fetchUserProfile = async () => {
        try {
            const profile = await getProfile();
            if (profile.error) {
                console.error("Error fetching profile:", profile.error);
                return;
            }
            this.setState({
                loggedInUserId: profile?._id || null
            });
        } catch (error) {
            console.error("Error fetching profile:", error.message);
        }
    }

    async fetchFileWorkflows() {
        try {
            if (!this.props.file || !this.props.file._id) {
                console.error('File ID is missing. Cannot fetch workflows.');
                return;
            }
            this.setState({ loading: true });
            let response = await getFileWorkflows(this.props.file._id);
            const workflows = response?.workflows || [];
            this.setState({ workflows: workflows });
        } catch (error) {
            console.error('Error fetching file workflows:', error);
        } finally {
            this.setState({ loading: false });
        }
    }

    updateStatus = async (containerId, stepName, status) => {
        try {
            this.setState({ updating: true });
            let backupWorkflows = JSON.parse(JSON.stringify(this.state.workflows)); // Deep copy for rollback
            // get workflows
            let workflows = await getFileWorkflows(this.props.file._id);
            workflows = workflows?.workflows || [];
            // find the workflow that contains the step
            let workflow = workflows.find(wf => wf.container == containerId);
            if (!workflow) {
                console.error('Workflow not found for container:', containerId);
                return;
            }
            // find the step
            let step = workflow.steps.find(s => s.name === stepName);
            if (!step) {
                console.error('Step not found:', stepName);
                return;
            }
            // mark the step as completed
            step.status = status;

            // If 'status' is 'completed', set all previous steps to 'completed' as well wherever the the user is assigned to the step
            if (status === "completed") {
                for (let i = 0; i < workflow.steps.length; i++) {
                    let s = workflow.steps[i];
                    if (s.name === stepName) {
                        break;
                    }
                    if (s.users && s.users.includes(this.state.loggedInUserId)) {
                        s.status = "completed";
                    }
                }
            }

            // update the workflow in the state
            this.setState(prevState => ({
                workflows: prevState.workflows.map(wf => wf.container === containerId ? workflow : wf)
            }));
            // Call the API to update the workflow on the server
            try {
                let response = await updateFileWorkflows(this.props.file._id, { workflows, container: containerId });
                if (response?.error) {
                    console.error('Error updating workflow:', response.error);
                    // Rollback to backup workflows
                    this.setState({ workflows: backupWorkflows });
                } else {
                    this.setState({ workflows: response?.workflows || [] });
                }
            } catch (error) {
                console.error('Error updating workflow:', error);
            }
        } catch (error) {
            console.error('Error marking step as completed:', error);
        } finally {
            this.setState({ updating: false });
        }
    }

    workflowRenderer(workflow) {
        const steps = workflow?.steps || [];
        return (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                {steps.map((step, index) => (
                    <React.Fragment key={index}>
                        <Button variant="outlined" size="small"
                            startIcon={(step.status == "completed") ? <TaskAltOutlinedIcon fontSize="small" color={this.state.updating ? "" : "primary"} /> : <CircleOutlinedIcon fontSize="small" color={this.state.updating ? "" : "primary"} />}
                            onClick={() => {
                                if (step.status !== "completed") {
                                    this.updateStatus(workflow.container, step.name, "completed");
                                } else {
                                    this.updateStatus(workflow.container, step.name, "pending");
                                }
                            }}
                            disabled={this.state.updating || (step.users && step.users.length > 0 && !step.users.includes(this.state.loggedInUserId))}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: { xs: '0.7rem', md: '1rem' }
                                }}
                            >
                                {step.name}
                            </Typography>
                        </Button>
                        {index < steps.length - 1 && (
                            <ArrowRightAltIcon fontSize="small" sx={{ mx: 0.5, opacity: this.state.updating ? 0.5 : 1, fontSize: { xs: '0.7rem', md: '1rem' } }} color={this.state.updating ? "" : "primary"} />
                        )}
                    </React.Fragment>
                ))}
            </Stack>
        );
    }

    render() {

        if (this.state.loading) {
            return (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ textAlign: 'center', justifyContent: 'center' }}>
                    <TextShimmerMotion text={"Loading workflows..."} />
                </Stack>
            );
        }

        if (!this.state.workflows || this.state.workflows.length === 0) {
            return null; // Don't render anything if there are no workflows
        }

        return (
            <Stack direction="column" spacing={2}>
                {this.state.workflows.map(workflow => this.workflowRenderer(workflow))}
            </Stack>
        );
    }
}

export default FileWorkflows;
